/**
 * Export Detailed Report - Full technical report generation for scan results
 *
 * Generates comprehensive reports including:
 * - Full AI insights with all findings
 * - Module-by-module detailed results
 * - Risk distribution and scoring factors
 * - CVE, EPSS, and KEV information
 */

import type {
  ScanResult,
  RiskLevel,
  RiskLevelCounts,
  Scan,
} from "./api";
import { calculateAdjustedDistribution, adjustFindingSeverity } from "./simplify-results";

// ============================================================================
// TYPES
// ============================================================================

export interface DetailedExportMetadata {
  target: string;
  scanId: string;
  mode: string;
  modules: string[];
  scannedAt: string;
  completedAt?: string;
  exportedAt: string;
}

export type DetailedExportFormat = "json" | "csv" | "html" | "pdf";

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatShortDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  };
  return text.replace(/[&<>"']/g, (char) => map[char]);
}

function formatModuleName(module: string): string {
  const names: Record<string, string> = {
    dns: "DNS Records",
    email: "Email Security",
    tls: "TLS/SSL Certificates",
    headers: "Security Headers",
    whois: "Domain WHOIS",
    tech: "Technologies",
    cloud: "Cloud Assets",
    asn: "Network Intelligence",
    repos: "Code Repositories",
    crawl_hints: "Crawl Analysis",
    js_analysis: "JavaScript Analysis",
    favicon: "Favicon Analysis",
    subdomains: "Subdomains",
    portscan: "Port Scan",
    ipintel: "IP Intelligence",
    typosquat: "Typosquatting",
  };
  return names[module] || module.charAt(0).toUpperCase() + module.slice(1).replace(/_/g, " ");
}

function formatIpCount(count: number): string {
  if (count >= 1_000_000_000) return `${(count / 1_000_000_000).toFixed(1)}B`;
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return count.toLocaleString();
}

// ============================================================================
// JSON EXPORT
// ============================================================================

function generateJson(
  results: ScanResult,
  metadata: DetailedExportMetadata
): string {
  const exportData = {
    report: {
      metadata,
      ai_insights: results.ai_insights,
      modules: results.modules,
    },
  };
  return JSON.stringify(exportData, null, 2);
}

// ============================================================================
// CSV EXPORT (Findings Table)
// ============================================================================

function generateCsv(
  results: ScanResult,
  metadata: DetailedExportMetadata
): string {
  const lines: string[] = [];

  lines.push(
    "Rank,Title,Description,Risk Level,Score,Confidence,Module,Asset ID,CVEs,EPSS,EPSS Percentile,Is KEV,Finding Type"
  );

  if (results.ai_insights?.findings) {
    for (const finding of results.ai_insights.findings) {
      const row = [
        finding.rank.toString(),
        `"${finding.title.replace(/"/g, '""')}"`,
        `"${finding.description.replace(/"/g, '""')}"`,
        finding.score.risk_level,
        finding.score.score.toFixed(1),
        finding.score.confidence.toFixed(2),
        finding.source_module,
        finding.asset_id ? `"${finding.asset_id}"` : "",
        finding.cve_ids.length > 0 ? `"${finding.cve_ids.join("; ")}"` : "",
        finding.epss ? finding.epss.epss.toFixed(4) : "",
        finding.epss ? finding.epss.percentile.toFixed(2) : "",
        finding.is_kev ? "true" : "false",
        finding.finding_type,
      ];
      lines.push(row.join(","));
    }
  }

  lines.push("");
  lines.push(`# Target: ${metadata.target}`);
  lines.push(`# Scan ID: ${metadata.scanId}`);
  lines.push(`# Mode: ${metadata.mode}`);
  lines.push(`# Modules: ${metadata.modules.join(", ")}`);
  lines.push(`# Scanned: ${metadata.scannedAt}`);
  if (results.ai_insights?.summary) {
    const s = results.ai_insights.summary;
    lines.push(`# Total Findings: ${s.total_findings}`);
    lines.push(`# Max Score: ${s.max_score.toFixed(1)}`);
    lines.push(`# Avg Score: ${s.avg_score.toFixed(1)}`);
  }

  return lines.join("\n");
}

// ============================================================================
// DESIGN SYSTEM
// ============================================================================

const RISK_THEME: Record<RiskLevel, { gradient: string; color: string; bg: string; light: string }> = {
  critical: { gradient: "linear-gradient(135deg, #dc2626 0%, #991b1b 100%)", color: "#dc2626", bg: "#fef2f2", light: "#fee2e2" },
  high: { gradient: "linear-gradient(135deg, #ea580c 0%, #c2410c 100%)", color: "#ea580c", bg: "#fff7ed", light: "#ffedd5" },
  medium: { gradient: "linear-gradient(135deg, #d97706 0%, #b45309 100%)", color: "#d97706", bg: "#fffbeb", light: "#fef3c7" },
  low: { gradient: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)", color: "#0891b2", bg: "#ecfeff", light: "#cffafe" },
  info: { gradient: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)", color: "#6366f1", bg: "#eef2ff", light: "#e0e7ff" },
};

function getStyles(): string {
  return `
    :root {
      --primary: #0f172a;
      --secondary: #64748b;
      --accent: #0891b2;
      --accent-light: #ecfeff;
      --success: #059669;
      --warning: #d97706;
      --danger: #dc2626;
      --border: #e2e8f0;
      --bg-subtle: #f8fafc;
      --radius: 12px;
      --radius-sm: 8px;
      --shadow: 0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06);
      --shadow-lg: 0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05);
    }

    * { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      max-width: 900px;
      margin: 0 auto;
      padding: 48px 24px;
      color: var(--primary);
      line-height: 1.6;
      background: #fff;
      -webkit-font-smoothing: antialiased;
    }

    /* Print styles - clean margins without browser headers/footers */
    @page {
      size: A4;
      margin: 20mm 18mm;  /* Top/bottom, left/right margins */
    }

    /* First page can have slightly different margins if needed */
    @page :first {
      margin-top: 15mm;
    }

    @media print {
      html {
        background: #fff !important;
      }

      body {
        margin: 0 !important;
        padding: 0 !important;
        max-width: none !important;
        background: #fff !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }

      .page-break { break-before: page; }
      .no-break { break-inside: avoid; }

      /* Hide any elements that shouldn't print */
      .no-print { display: none !important; }

      /* Ensure backgrounds and colors print */
      .badge, .stat-card, .card, .tag, .progress-fill, .finding-card, .code-block {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }

      /* Prevent widows/orphans */
      p, li, h1, h2, h3, h4 {
        orphans: 3;
        widows: 3;
      }

      /* Ensure links don't show URLs */
      a[href]:after { content: none !important; }

      /* Adjust section spacing for print */
      .section { margin-bottom: 24px; }
      .card { box-shadow: none; border: 1px solid #e2e8f0; }
    }

    /* Typography */
    h1 { font-size: 28px; font-weight: 700; letter-spacing: -0.5px; }
    h2 { font-size: 20px; font-weight: 600; letter-spacing: -0.3px; color: var(--primary); }
    h3 { font-size: 14px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: var(--secondary); }

    .mono { font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace; }
    .text-sm { font-size: 13px; }
    .text-xs { font-size: 11px; }
    .text-muted { color: var(--secondary); }

    /* Layout */
    .section { margin-bottom: 40px; }
    .card {
      background: #fff;
      border: 1px solid var(--border);
      border-radius: var(--radius);
      padding: 24px;
      box-shadow: var(--shadow);
    }
    .card-compact { padding: 16px; }
    .grid-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
    .grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
    .grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
    .flex { display: flex; }
    .flex-wrap { flex-wrap: wrap; }
    .flex-between { justify-content: space-between; }
    .flex-center { align-items: center; }
    .gap-1 { gap: 4px; }
    .gap-2 { gap: 8px; }
    .gap-3 { gap: 12px; }
    .gap-4 { gap: 16px; }
    .mt-2 { margin-top: 8px; }
    .mt-3 { margin-top: 12px; }
    .mt-4 { margin-top: 16px; }
    .mb-2 { margin-bottom: 8px; }
    .mb-3 { margin-bottom: 12px; }
    .mb-4 { margin-bottom: 16px; }

    /* Components */
    .badge {
      display: inline-flex;
      align-items: center;
      padding: 4px 10px;
      border-radius: 20px;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }
    .badge-critical { background: #fef2f2; color: #dc2626; }
    .badge-high { background: #fff7ed; color: #ea580c; }
    .badge-medium { background: #fffbeb; color: #d97706; }
    .badge-low { background: #ecfeff; color: #0891b2; }
    .badge-info { background: #eef2ff; color: #6366f1; }
    .badge-success { background: #ecfdf5; color: #059669; }
    .badge-danger { background: #fef2f2; color: #dc2626; }
    .badge-outline { background: transparent; border: 1px solid var(--border); color: var(--secondary); }

    .tag {
      display: inline-block;
      padding: 2px 8px;
      background: var(--bg-subtle);
      border-radius: 4px;
      font-size: 11px;
      font-family: 'SF Mono', monospace;
      color: var(--secondary);
    }

    .stat-card {
      text-align: center;
      padding: 20px;
      background: var(--bg-subtle);
      border-radius: var(--radius-sm);
    }
    .stat-value {
      font-size: 32px;
      font-weight: 700;
      line-height: 1.2;
      font-family: 'SF Mono', monospace;
    }
    .stat-label {
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: var(--secondary);
      margin-top: 4px;
    }

    .divider {
      height: 1px;
      background: var(--border);
      margin: 24px 0;
    }

    .code-block {
      background: var(--bg-subtle);
      border: 1px solid var(--border);
      border-radius: var(--radius-sm);
      padding: 12px 16px;
      font-family: 'SF Mono', monospace;
      font-size: 12px;
      overflow-x: auto;
      word-break: break-all;
    }

    /* Data Table */
    .data-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
    }
    .data-table th {
      text-align: left;
      padding: 12px 16px;
      background: var(--bg-subtle);
      border-bottom: 2px solid var(--border);
      font-weight: 600;
      text-transform: uppercase;
      font-size: 10px;
      letter-spacing: 0.5px;
      color: var(--secondary);
    }
    .data-table td {
      padding: 12px 16px;
      border-bottom: 1px solid var(--border);
      vertical-align: top;
    }
    .data-table tr:last-child td { border-bottom: none; }

    /* Finding Card */
    .finding-card {
      border: 1px solid var(--border);
      border-radius: var(--radius-sm);
      padding: 16px;
      margin-bottom: 12px;
      transition: border-color 0.2s;
    }
    .finding-card.critical { border-left: 4px solid #dc2626; }
    .finding-card.high { border-left: 4px solid #ea580c; }
    .finding-card.medium { border-left: 4px solid #d97706; }
    .finding-card.low { border-left: 4px solid #0891b2; }
    .finding-card.info { border-left: 4px solid #6366f1; }

    .finding-rank {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: 12px;
      flex-shrink: 0;
    }

    /* Module Section */
    .module-section {
      border: 1px solid var(--border);
      border-radius: var(--radius);
      overflow: hidden;
      margin-bottom: 20px;
    }
    .module-header {
      background: var(--bg-subtle);
      padding: 16px 20px;
      border-bottom: 1px solid var(--border);
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    .module-content { padding: 20px; }

    /* Data Row */
    .data-row {
      display: flex;
      padding: 10px 0;
      border-bottom: 1px solid #f1f5f9;
    }
    .data-row:last-child { border-bottom: none; }
    .data-label {
      width: 140px;
      flex-shrink: 0;
      font-size: 12px;
      color: var(--secondary);
      font-weight: 500;
    }
    .data-value {
      flex: 1;
      font-size: 13px;
    }

    /* Progress Bar */
    .progress-bar {
      height: 8px;
      background: #f1f5f9;
      border-radius: 4px;
      overflow: hidden;
    }
    .progress-fill {
      height: 100%;
      border-radius: 4px;
      transition: width 0.3s;
    }

    /* Score Gauge */
    .score-gauge {
      width: 120px;
      height: 120px;
      position: relative;
      margin: 0 auto;
    }
    .score-gauge svg {
      transform: rotate(-90deg);
    }
    .score-gauge .bg {
      fill: none;
      stroke: #f1f5f9;
      stroke-width: 8;
    }
    .score-gauge .fill {
      fill: none;
      stroke-width: 8;
      stroke-linecap: round;
      transition: stroke-dashoffset 0.5s;
    }
    .score-text {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
    }
    .score-number {
      font-size: 28px;
      font-weight: 700;
      font-family: 'SF Mono', monospace;
      line-height: 1;
    }

    /* Item List */
    .item-list {
      list-style: none;
    }
    .item-list li {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      padding: 8px 0;
      border-bottom: 1px solid #f1f5f9;
    }
    .item-list li:last-child { border-bottom: none; }

    /* Status Indicator */
    .status-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      flex-shrink: 0;
      margin-top: 4px;
    }
    .status-dot.success { background: #059669; }
    .status-dot.warning { background: #d97706; }
    .status-dot.danger { background: #dc2626; }
    .status-dot.info { background: #6366f1; }
  `;
}

// ============================================================================
// HTML GENERATION
// ============================================================================

function generateHtml(
  results: ScanResult,
  metadata: DetailedExportMetadata
): string {
  const insights = results.ai_insights;
  const summary = insights?.summary;

  const headerHtml = generateHeaderHtml(metadata);
  const riskSummaryHtml = summary ? generateRiskSummaryHtml(summary, insights?.findings ?? [], insights?.source) : "";
  const findingsHtml = insights?.findings?.length ? generateFindingsHtml(insights.findings) : "";
  const modulesHtml = generateModulesHtml(results.modules, metadata.modules);
  const footerHtml = generateFooterHtml(metadata);

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Security Report - ${escapeHtml(metadata.target)}</title>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
  <style>${getStyles()}</style>
</head>
<body>
  ${headerHtml}
  ${riskSummaryHtml}
  ${findingsHtml}
  ${modulesHtml}
  ${footerHtml}
</body>
</html>`;
}

function generateHeaderHtml(metadata: DetailedExportMetadata): string {
  return `
    <header class="section" style="text-align: center; padding-bottom: 32px; border-bottom: 2px solid var(--border);">
      <div style="display: inline-block; padding: 8px 16px; background: var(--accent-light); border-radius: 20px; margin-bottom: 16px;">
        <span class="text-sm" style="color: var(--accent); font-weight: 600;">Security Assessment Report</span>
      </div>
      <h1 class="mono" style="margin-bottom: 8px;">${escapeHtml(metadata.target)}</h1>
      <div class="flex flex-center gap-4" style="justify-content: center; margin-top: 16px;">
        <span class="tag">${escapeHtml(metadata.mode.toUpperCase())}</span>
        <span class="text-sm text-muted">${formatShortDate(metadata.scannedAt)}</span>
        <span class="text-sm text-muted">${metadata.modules.length} modules</span>
      </div>
    </header>
  `;
}

function generateRiskSummaryHtml(
  summary: NonNullable<ScanResult["ai_insights"]>["summary"],
  findings: NonNullable<ScanResult["ai_insights"]>["findings"],
  source?: string
): string {
  // Use the same adjusted-distribution logic as the web UI so both views agree.
  // Raw max_score alone can't reflect severity upgrades (e.g. exposure_known_vulnerability
  // is always critical regardless of its numeric score).
  const adjustedDist = calculateAdjustedDistribution(findings ?? []) as RiskLevelCounts;
  const maxRisk: RiskLevel =
    adjustedDist.critical > 0 ? "critical" :
    adjustedDist.high     > 0 ? "high"     :
    adjustedDist.medium   > 0 ? "medium"   :
    adjustedDist.low      > 0 ? "low"      : "info";
  const theme = RISK_THEME[maxRisk];
  const urgentCount = adjustedDist.critical + adjustedDist.high;

  // Floor the display score to the minimum for the adjusted risk level so the
  // gauge and badge always agree. E.g. if type-based rules push a 5.0 finding
  // to "critical", show ≥ 9.0 on the ring rather than a contradictory 5.0.
  const riskFloor: Record<RiskLevel, number> = {
    critical: 9.0, high: 7.0, medium: 4.0, low: 1.0, info: 0.0,
  };
  const displayScore = Math.max(summary.max_score, riskFloor[maxRisk]);

  const circumference = 2 * Math.PI * 52;
  const offset = circumference - (displayScore / 10) * circumference;

  return `
    <section class="section">
      <h3 class="mb-4">Risk Overview</h3>
      <div class="card">
        <div class="grid-3 gap-4">
          <!-- Score Gauge -->
          <div style="text-align: center;">
            <div class="score-gauge">
              <svg width="120" height="120" viewBox="0 0 120 120">
                <circle class="bg" cx="60" cy="60" r="52"/>
                <circle class="fill" cx="60" cy="60" r="52"
                  stroke="${theme.color}"
                  stroke-dasharray="${circumference}"
                  stroke-dashoffset="${offset}"/>
              </svg>
              <div class="score-text">
                <div class="score-number" style="color: ${theme.color};">${displayScore.toFixed(1)}</div>
                <div class="text-xs text-muted">of 10</div>
              </div>
            </div>
            <div class="badge badge-${maxRisk} mt-3">${maxRisk} Risk</div>
          </div>

          <!-- Risk Distribution -->
          <div>
            <div class="text-sm font-medium mb-3">Risk Distribution</div>
            ${generateRiskBarsHtml(adjustedDist, summary.total_findings)}
          </div>

          <!-- Quick Stats -->
          <div>
            <div class="grid-2 gap-2">
              <div class="stat-card">
                <div class="stat-value" style="color: var(--primary);">${summary.total_findings}</div>
                <div class="stat-label">Findings</div>
              </div>
              <div class="stat-card">
                <div class="stat-value" style="color: ${urgentCount > 0 ? "var(--danger)" : "var(--success)"};">${urgentCount}</div>
                <div class="stat-label">Urgent</div>
              </div>
              <div class="stat-card">
                <div class="stat-value">${summary.cve_findings}</div>
                <div class="stat-label">CVEs</div>
              </div>
              <div class="stat-card">
                <div class="stat-value" style="color: ${summary.kev_listed > 0 ? "var(--danger)" : "var(--secondary)"};">${summary.kev_listed}</div>
                <div class="stat-label">KEV</div>
              </div>
            </div>
          </div>
        </div>

        <div class="divider"></div>

        <div class="flex flex-between flex-center text-sm text-muted">
          <div class="flex gap-4">
            <span>Avg Score: <strong class="mono">${summary.avg_score.toFixed(1)}</strong></span>
            <span>EPSS Enriched: <strong class="mono">${summary.epss_enriched}</strong></span>
          </div>
          <span class="badge badge-outline">${source === "local_ml" ? "ML-Enhanced" : "Rule-Based"}</span>
        </div>
      </div>
    </section>
  `;
}

function generateRiskBarsHtml(counts: RiskLevelCounts, total: number): string {
  const levels: Array<{ key: RiskLevel; label: string }> = [
    { key: "critical", label: "Critical" },
    { key: "high", label: "High" },
    { key: "medium", label: "Medium" },
    { key: "low", label: "Low" },
    { key: "info", label: "Info" },
  ];

  return levels.map(({ key, label }) => {
    const count = counts[key] || 0;
    const pct = total > 0 ? (count / total) * 100 : 0;
    const theme = RISK_THEME[key];

    return `
      <div class="flex flex-center gap-2 mb-2">
        <span class="text-xs" style="width: 50px; color: var(--secondary);">${label}</span>
        <div class="progress-bar" style="flex: 1;">
          <div class="progress-fill" style="width: ${Math.max(pct, count > 0 ? 3 : 0)}%; background: ${theme.color};"></div>
        </div>
        <span class="mono text-xs" style="width: 24px; text-align: right;">${count}</span>
      </div>
    `;
  }).join("");
}

function generateFindingsHtml(findings: NonNullable<ScanResult["ai_insights"]>["findings"]): string {
  const findingCards = findings.slice(0, 20).map((f) => {
    const severity = adjustFindingSeverity(f);
    const theme = RISK_THEME[severity];

    return `
      <div class="finding-card ${severity} no-break">
        <div class="flex gap-3">
          <div class="finding-rank" style="background: ${theme.light}; color: ${theme.color};">
            ${f.rank}
          </div>
          <div style="flex: 1; min-width: 0;">
            <div class="flex flex-between flex-center mb-2">
              <h4 style="font-size: 14px; font-weight: 600; margin: 0;">${escapeHtml(f.title)}</h4>
              <div class="flex gap-2 flex-center">
                <span class="mono text-sm" style="color: ${theme.color}; font-weight: 600;">${f.score.score.toFixed(1)}</span>
                <span class="badge badge-${severity}">${severity}</span>
              </div>
            </div>
            <p class="text-sm text-muted mb-2">${escapeHtml(f.description)}</p>
            <div class="flex gap-2 flex-center flex-wrap">
              <span class="tag">${escapeHtml(f.source_module)}</span>
              ${f.asset_id ? `<span class="tag mono">${escapeHtml(f.asset_id)}</span>` : ""}
              ${f.cve_ids.length > 0 ? f.cve_ids.map(cve => `<span class="badge" style="background: #fffbeb; color: #d97706; font-size: 10px;">${escapeHtml(cve)}</span>`).join("") : ""}
              ${f.is_kev ? '<span class="badge badge-danger" style="font-size: 10px;">KEV</span>' : ""}
            </div>
          </div>
        </div>
      </div>
    `;
  }).join("");

  return `
    <section class="section page-break">
      <h3 class="mb-4">Security Findings (${findings.length})</h3>
      ${findingCards}
      ${findings.length > 20 ? `<p class="text-sm text-muted mt-4">... and ${findings.length - 20} more findings</p>` : ""}
    </section>
  `;
}

function generateModulesHtml(modules: Record<string, unknown>, moduleList: string[]): string {
  const moduleCards = moduleList
    .filter((mod) => modules[mod] && !hasModuleError(modules[mod]))
    .map((mod) => {
      const data = modules[mod];
      const content = getModuleContentHtml(mod, data);

      return `
        <div class="module-section no-break">
          <div class="module-header">
            <h4 style="font-size: 15px; font-weight: 600; margin: 0;">${formatModuleName(mod)}</h4>
            <span class="tag">${mod}</span>
          </div>
          <div class="module-content">
            ${content}
          </div>
        </div>
      `;
    })
    .join("");

  if (!moduleCards) return "";

  return `
    <section class="section page-break">
      <h3 class="mb-4">Module Results</h3>
      ${moduleCards}
    </section>
  `;
}

function generateFooterHtml(metadata: DetailedExportMetadata): string {
  return `
    <footer style="text-align: center; padding-top: 32px; border-top: 1px solid var(--border); color: var(--secondary); font-size: 12px;">
      <p>Generated by <strong>ASM Toolkit</strong> · ${formatDate(metadata.exportedAt)}</p>
      <p class="mono mt-2" style="font-size: 10px; opacity: 0.7;">Scan ID: ${metadata.scanId}</p>
    </footer>
  `;
}

// ============================================================================
// MODULE CONTENT GENERATORS
// ============================================================================

function hasModuleError(data: unknown): boolean {
  if (!data || typeof data !== "object") return false;
  return "error" in (data as Record<string, unknown>);
}

function getModuleContentHtml(module: string, data: unknown): string {
  if (!data || typeof data !== "object") return '<p class="text-sm text-muted">No data available</p>';
  const d = data as Record<string, unknown>;

  switch (module) {
    case "dns": return generateDnsHtml(d);
    case "email": return generateEmailHtml(d);
    case "tls": return generateTlsHtml(d);
    case "headers": return generateHeadersHtml(d);
    case "whois": return generateWhoisHtml(d);
    case "subdomains": return generateSubdomainsHtml(d);
    case "portscan": return generatePortscanHtml(d);
    case "cloud": return generateCloudHtml(d);
    case "repos": return generateReposHtml(d);
    case "tech": return generateTechHtml(d);
    case "asn": return generateAsnHtml(d);
    case "crawl_hints": return generateCrawlHintsHtml(d);
    case "js_analysis": return generateJsAnalysisHtml(d);
    case "favicon": return generateFaviconHtml(d);
    case "ipintel": return generateIpIntelHtml(d);
    case "typosquat": return generateTyposquatHtml(d);
    default: return generateGenericHtml(d);
  }
}

// DNS Module
function generateDnsHtml(d: Record<string, unknown>): string {
  const records = Array.isArray(d.records) ? d.records : [];
  if (records.length === 0) return '<p class="text-sm text-muted">No DNS records found</p>';

  const grouped: Record<string, Array<{ name?: string; value?: string; ttl?: number }>> = {};
  records.forEach((r: { type?: string; name?: string; value?: string; ttl?: number }) => {
    const type = r.type || "OTHER";
    if (!grouped[type]) grouped[type] = [];
    grouped[type].push(r);
  });

  return `
    <table class="data-table">
      <thead>
        <tr><th>Type</th><th>Value</th><th>TTL</th></tr>
      </thead>
      <tbody>
        ${Object.entries(grouped).slice(0, 12).flatMap(([type, recs]) =>
          recs.slice(0, 5).map(r => `
            <tr>
              <td><span class="badge badge-info">${type}</span></td>
              <td><code class="text-xs">${escapeHtml(String(r.value || r.name || ""))}</code></td>
              <td class="text-muted">${r.ttl || "-"}</td>
            </tr>
          `)
        ).join("")}
      </tbody>
    </table>
    <p class="text-xs text-muted mt-3">${records.length} total records</p>
  `;
}

// Email Module
function generateEmailHtml(d: Record<string, unknown>): string {
  const spf = d.spf as { record?: string; valid?: boolean } | undefined;
  const dkim = d.dkim as { selectors?: Array<{ selector?: string; valid?: boolean }> } | undefined;
  const dmarc = d.dmarc as { record?: string; policy?: string } | undefined;

  const statusIcon = (valid: boolean | undefined) =>
    valid ? '<span style="color: var(--success);">✓</span>' : '<span style="color: var(--danger);">✗</span>';

  const policyColor = (policy?: string) => {
    if (policy === "reject") return "badge-success";
    if (policy === "quarantine") return "badge-medium";
    return "badge-danger";
  };

  return `
    <div class="grid-3 gap-3">
      <div class="card card-compact" style="border-left: 3px solid ${spf?.record ? "var(--success)" : "var(--danger)"};">
        <div class="flex flex-between flex-center mb-2">
          <strong class="text-sm">SPF Record</strong>
          ${statusIcon(!!spf?.record)}
        </div>
        ${spf?.record
          ? `<code class="text-xs" style="display: block; word-break: break-all;">${escapeHtml(spf.record.substring(0, 100))}${spf.record.length > 100 ? "..." : ""}</code>`
          : '<span class="badge badge-danger">Not configured</span>'}
      </div>
      <div class="card card-compact" style="border-left: 3px solid ${dmarc?.record ? "var(--success)" : "var(--danger)"};">
        <div class="flex flex-between flex-center mb-2">
          <strong class="text-sm">DMARC Policy</strong>
          ${statusIcon(!!dmarc?.record)}
        </div>
        ${dmarc?.policy
          ? `<span class="badge ${policyColor(dmarc.policy)}">${dmarc.policy}</span>`
          : '<span class="badge badge-danger">Not configured</span>'}
      </div>
      <div class="card card-compact" style="border-left: 3px solid ${dkim?.selectors?.length ? "var(--success)" : "var(--warning)"};">
        <div class="flex flex-between flex-center mb-2">
          <strong class="text-sm">DKIM</strong>
          ${statusIcon(!!dkim?.selectors?.length)}
        </div>
        ${dkim?.selectors?.length
          ? `<span class="badge badge-success">${dkim.selectors.length} selector${dkim.selectors.length > 1 ? "s" : ""}</span>`
          : '<span class="badge badge-outline">Not found</span>'}
      </div>
    </div>
  `;
}

// TLS Module
function generateTlsHtml(d: Record<string, unknown>): string {
  const certs = Array.isArray(d.certificates) ? d.certificates : [];
  if (certs.length === 0) return '<p class="text-sm text-muted">No certificates found</p>';

  return `
    <table class="data-table">
      <thead>
        <tr><th>Subject</th><th>Issuer</th><th>Expires</th><th>Status</th></tr>
      </thead>
      <tbody>
        ${certs.slice(0, 5).map((cert: {
          subject?: string;
          issuer?: string;
          not_after?: string;
          is_expired?: boolean;
          days_until_expiry?: number;
        }) => {
          const isExpired = cert.is_expired;
          const isWarning = !isExpired && cert.days_until_expiry !== undefined && cert.days_until_expiry < 30;
          return `
            <tr>
              <td><code class="text-xs">${escapeHtml(cert.subject || "Unknown")}</code></td>
              <td class="text-muted text-xs">${escapeHtml(cert.issuer || "Unknown")}</td>
              <td>${cert.not_after ? formatShortDate(cert.not_after) : "Unknown"}</td>
              <td>
                ${isExpired
                  ? '<span class="badge badge-danger">Expired</span>'
                  : isWarning
                    ? `<span class="badge badge-medium">${cert.days_until_expiry}d left</span>`
                    : '<span class="badge badge-success">Valid</span>'}
              </td>
            </tr>
          `;
        }).join("")}
      </tbody>
    </table>
  `;
}

// Headers Module
function generateHeadersHtml(d: Record<string, unknown>): string {
  const results = Array.isArray(d.results) ? d.results : [];
  if (results.length === 0) return '<p class="text-sm text-muted">No header analysis available</p>';

  return results.slice(0, 2).map((r: { url?: string; missing_headers?: string[]; present_headers?: Record<string, string> }) => {
    const missing = r.missing_headers || [];
    const present = Object.keys(r.present_headers || {});

    return `
      <div class="mb-4">
        <code class="text-xs text-muted">${escapeHtml(r.url || "")}</code>
        ${missing.length > 0 ? `
          <div class="mt-3">
            <p class="text-xs mb-2" style="color: var(--danger); font-weight: 600;">Missing Security Headers (${missing.length})</p>
            <div class="flex gap-1 flex-wrap">
              ${missing.map(h => `<span class="badge badge-danger" style="font-size: 10px;">${escapeHtml(h)}</span>`).join("")}
            </div>
          </div>
        ` : '<p class="badge badge-success mt-3">All recommended headers present</p>'}
        ${present.length > 0 ? `
          <div class="mt-3">
            <p class="text-xs mb-2" style="color: var(--success); font-weight: 600;">Present (${present.length})</p>
            <div class="flex gap-1 flex-wrap">
              ${present.slice(0, 10).map(h => `<span class="tag" style="font-size: 10px;">${escapeHtml(h)}</span>`).join("")}
              ${present.length > 10 ? `<span class="text-xs text-muted">+${present.length - 10}</span>` : ""}
            </div>
          </div>
        ` : ""}
      </div>
    `;
  }).join('<div class="divider"></div>');
}

// WHOIS Module
function generateWhoisHtml(d: Record<string, unknown>): string {
  const fields = [
    { key: "registrar", label: "Registrar" },
    { key: "creation_date", label: "Created" },
    { key: "expiration_date", label: "Expires" },
    { key: "registrant_org", label: "Organization" },
    { key: "name_servers", label: "Nameservers" },
  ];

  const rows = fields
    .filter(f => d[f.key])
    .map(f => {
      let value = d[f.key];
      if (Array.isArray(value)) value = value.slice(0, 3).join(", ");
      if (f.key.includes("date") && typeof value === "string") {
        try { value = formatShortDate(value); } catch { /* keep original */ }
      }
      return `
        <div class="data-row">
          <div class="data-label">${f.label}</div>
          <div class="data-value">${escapeHtml(String(value))}</div>
        </div>
      `;
    })
    .join("");

  return rows || '<p class="text-sm text-muted">No WHOIS data available</p>';
}

// Subdomains Module
function generateSubdomainsHtml(d: Record<string, unknown>): string {
  const subs = Array.isArray(d.subdomains) ? d.subdomains : [];
  if (subs.length === 0) return '<p class="text-sm text-muted">No subdomains discovered</p>';

  return `
    <div class="flex gap-1 flex-wrap mb-3">
      ${subs.slice(0, 30).map((s: { name?: string; subdomain?: string }) =>
        `<span class="tag">${escapeHtml(s.name || s.subdomain || String(s))}</span>`
      ).join("")}
    </div>
    ${subs.length > 30 ? `<p class="text-xs text-muted">... and ${subs.length - 30} more subdomains</p>` : ""}
    <div class="badge badge-outline mt-3">${subs.length} total subdomains</div>
  `;
}

// Portscan Module
function generatePortscanHtml(d: Record<string, unknown>): string {
  const tcpPorts = Array.isArray(d.tcp_ports) ? d.tcp_ports : [];
  const openPorts = tcpPorts.filter((p: { state?: string }) => p.state === "open");

  if (openPorts.length === 0) return '<span class="badge badge-success">No open ports detected</span>';

  const sensitivePorts = [21, 22, 23, 25, 3389, 5900, 1433, 3306, 5432, 27017, 6379];

  return `
    <table class="data-table">
      <thead>
        <tr><th>Port</th><th>Service</th><th>Risk</th></tr>
      </thead>
      <tbody>
        ${openPorts.slice(0, 20).map((p: { port?: number; service_hint?: string }) => {
          const isSensitive = p.port && sensitivePorts.includes(p.port);
          return `
            <tr>
              <td><code class="mono" style="${isSensitive ? "color: var(--danger); font-weight: 600;" : ""}">${p.port}</code></td>
              <td>${escapeHtml(p.service_hint || "unknown")}</td>
              <td>${isSensitive ? '<span class="badge badge-danger">Sensitive</span>' : '<span class="badge badge-info">Standard</span>'}</td>
            </tr>
          `;
        }).join("")}
      </tbody>
    </table>
    ${openPorts.length > 20 ? `<p class="text-xs text-muted mt-2">... and ${openPorts.length - 20} more ports</p>` : ""}
    <div class="badge badge-outline mt-3">${openPorts.length} open ports</div>
  `;
}

// Cloud Module
function generateCloudHtml(d: Record<string, unknown>): string {
  const assets = Array.isArray(d.assets) ? d.assets : [];
  if (assets.length === 0) return '<p class="text-sm text-muted">No cloud assets identified</p>';

  return `
    <table class="data-table">
      <thead>
        <tr><th>Provider</th><th>Asset</th><th>Access</th></tr>
      </thead>
      <tbody>
        ${assets.slice(0, 10).map((a: { provider?: string; type?: string; name?: string; url?: string; access_level?: string }) => {
          const isPublic = a.access_level === "public_read" || a.access_level === "public_write";
          return `
            <tr>
              <td><span class="badge badge-info">${escapeHtml(a.provider || "unknown")}</span></td>
              <td><code class="text-xs">${escapeHtml(a.name || a.url || "Unknown")}</code></td>
              <td>${isPublic ? '<span class="badge badge-danger">Public</span>' : '<span class="badge badge-success">Private</span>'}</td>
            </tr>
          `;
        }).join("")}
      </tbody>
    </table>
    ${assets.length > 10 ? `<p class="text-xs text-muted mt-2">... and ${assets.length - 10} more assets</p>` : ""}
  `;
}

// Repos Module
function generateReposHtml(d: Record<string, unknown>): string {
  const repos = Array.isArray(d.repositories) ? d.repositories : [];
  const secrets = Array.isArray(d.secrets) ? d.secrets : [];

  let html = "";

  if (secrets.length > 0) {
    html += `
      <div class="card card-compact mb-4" style="border: 1px solid var(--danger); background: #fef2f2;">
        <div class="flex flex-between flex-center mb-3">
          <strong style="color: var(--danger);">⚠ Exposed Secrets Detected</strong>
          <span class="badge badge-danger">${secrets.length}</span>
        </div>
        <table class="data-table">
          <thead><tr><th>Type</th><th>File</th><th>Severity</th></tr></thead>
          <tbody>
            ${secrets.slice(0, 5).map((s: { type?: string; file?: string; severity?: string }) => `
              <tr>
                <td><span class="badge badge-danger" style="font-size: 10px;">${escapeHtml(s.type || "secret")}</span></td>
                <td><code class="text-xs">${escapeHtml(s.file || "")}</code></td>
                <td><span class="badge badge-${s.severity === "critical" ? "critical" : "high"}">${s.severity || "high"}</span></td>
              </tr>
            `).join("")}
          </tbody>
        </table>
      </div>
    `;
  }

  if (repos.length > 0) {
    html += `
      <div class="flex gap-1 flex-wrap">
        ${repos.slice(0, 10).map((r: { name?: string; url?: string }) =>
          `<span class="tag">${escapeHtml(r.name || r.url || String(r))}</span>`
        ).join("")}
      </div>
      <div class="badge badge-outline mt-3">${repos.length} repositories found</div>
    `;
  }

  return html || '<p class="text-sm text-muted">No repositories found</p>';
}

// Tech Module
function generateTechHtml(d: Record<string, unknown>): string {
  const techs = Array.isArray(d.technologies) ? d.technologies : [];
  if (techs.length === 0) return '<p class="text-sm text-muted">No technologies detected</p>';

  const byCategory: Record<string, Array<{ name?: string; version?: string }>> = {};
  techs.forEach((t: { name?: string; category?: string; version?: string }) => {
    const cat = t.category || "Other";
    if (!byCategory[cat]) byCategory[cat] = [];
    byCategory[cat].push(t);
  });

  return Object.entries(byCategory).slice(0, 8).map(([category, items]) => `
    <div class="mb-3">
      <p class="text-xs text-muted mb-2 font-medium">${escapeHtml(category)}</p>
      <div class="flex gap-1 flex-wrap">
        ${items.slice(0, 8).map(t => `
          <span class="tag">${escapeHtml(t.name || "Unknown")}${t.version ? ` <span class="text-muted">${escapeHtml(t.version)}</span>` : ""}</span>
        `).join("")}
        ${items.length > 8 ? `<span class="text-xs text-muted">+${items.length - 8}</span>` : ""}
      </div>
    </div>
  `).join("");
}

// ASN Module
function generateAsnHtml(d: Record<string, unknown>): string {
  const asns = Array.isArray(d.asns) ? d.asns : [];
  const prefixes = Array.isArray(d.prefixes) ? d.prefixes : [];
  const totalIps = d.total_ips as number || prefixes.reduce((sum: number, p: { num_ips?: number }) => sum + (p.num_ips || 0), 0);

  const summaryHtml = `
    <div class="grid-4 gap-3 mb-4">
      <div class="stat-card">
        <div class="stat-value" style="font-size: 24px;">${asns.length}</div>
        <div class="stat-label">ASNs</div>
      </div>
      <div class="stat-card">
        <div class="stat-value" style="font-size: 24px; color: var(--accent);">${prefixes.length}</div>
        <div class="stat-label">Prefixes</div>
      </div>
      <div class="stat-card">
        <div class="stat-value" style="font-size: 24px; color: var(--accent);">${formatIpCount(totalIps)}</div>
        <div class="stat-label">Total IPs</div>
      </div>
      <div class="stat-card">
        <div class="stat-value" style="font-size: 24px; color: ${d.is_shared_hosting ? "var(--warning)" : "var(--success)"};">${d.is_shared_hosting ? "Yes" : "No"}</div>
        <div class="stat-label">Shared Host</div>
      </div>
    </div>
  `;

  if (asns.length === 0) return summaryHtml + '<p class="text-sm text-muted">No ASN data available</p>';

  return summaryHtml + `
    <table class="data-table">
      <thead><tr><th>ASN</th><th>Name</th><th>Country</th><th>RIR</th></tr></thead>
      <tbody>
        ${asns.slice(0, 10).map((asn: { asn?: number; name?: string; country_code?: string; rir?: string }) => `
          <tr>
            <td><code class="mono">AS${asn.asn}</code></td>
            <td>${escapeHtml(asn.name || "Unknown")}</td>
            <td>${escapeHtml(asn.country_code || "-")}</td>
            <td><span class="badge badge-outline">${escapeHtml(asn.rir || "-")}</span></td>
          </tr>
        `).join("")}
      </tbody>
    </table>
  `;
}

// Crawl Hints Module
function generateCrawlHintsHtml(d: Record<string, unknown>): string {
  const robots = d.robots as { found?: boolean; rules?: unknown[]; sensitive_paths?: unknown[] } | undefined;
  const securityTxt = d.security_txt as { found?: boolean; is_valid?: boolean; contact?: string[] } | undefined;
  const sitemaps = Array.isArray(d.sitemaps) ? d.sitemaps : [];

  const discoveryHtml = `
    <div class="grid-4 gap-3 mb-4">
      <div class="stat-card">
        <div style="font-size: 20px;">${robots?.found ? '<span style="color: var(--success);">✓</span>' : '<span style="color: var(--secondary);">✗</span>'}</div>
        <div class="stat-label">robots.txt</div>
      </div>
      <div class="stat-card">
        <div style="font-size: 20px;">${securityTxt?.found ? '<span style="color: var(--success);">✓</span>' : '<span style="color: var(--warning);">✗</span>'}</div>
        <div class="stat-label">security.txt</div>
      </div>
      <div class="stat-card">
        <div style="font-size: 20px;">${sitemaps.some((s: { found?: boolean }) => s.found) ? '<span style="color: var(--success);">✓</span>' : '<span style="color: var(--secondary);">✗</span>'}</div>
        <div class="stat-label">sitemap.xml</div>
      </div>
      <div class="stat-card">
        <div class="stat-value" style="font-size: 24px; color: ${(robots?.sensitive_paths as unknown[] || []).length > 0 ? 'var(--warning)' : 'var(--success)'};">${(robots?.sensitive_paths as unknown[] || []).length}</div>
        <div class="stat-label">Sensitive Paths</div>
      </div>
    </div>
  `;

  let detailsHtml = "";

  if (securityTxt?.found) {
    detailsHtml += `
      <div class="card card-compact mb-3" style="border-left: 3px solid ${securityTxt.is_valid ? "var(--success)" : "var(--warning)"};">
        <div class="flex flex-between flex-center mb-2">
          <strong class="text-sm">security.txt</strong>
          <span class="badge ${securityTxt.is_valid ? "badge-success" : "badge-medium"}">${securityTxt.is_valid ? "Valid" : "Invalid"}</span>
        </div>
        ${securityTxt.contact && securityTxt.contact.length > 0 ? `<p class="text-xs text-muted">Contact: ${escapeHtml(securityTxt.contact[0])}</p>` : ""}
      </div>
    `;
  }

  const sensitivePaths = robots?.sensitive_paths as Array<{ path?: string; severity?: string; reason?: string }> || [];
  if (sensitivePaths.length > 0) {
    detailsHtml += `
      <div class="mt-4">
        <p class="text-xs mb-2" style="color: var(--warning); font-weight: 600;">Sensitive Paths Found</p>
        <ul class="item-list">
          ${sensitivePaths.slice(0, 8).map(p => `
            <li>
              <div class="status-dot warning"></div>
              <div>
                <code class="text-xs">${escapeHtml(p.path || "")}</code>
                <p class="text-xs text-muted">${escapeHtml(p.reason || "")}</p>
              </div>
            </li>
          `).join("")}
        </ul>
      </div>
    `;
  }

  return discoveryHtml + detailsHtml;
}

// JS Analysis Module
function generateJsAnalysisHtml(d: Record<string, unknown>): string {
  const jsFiles = Array.isArray(d.js_files_analyzed) ? d.js_files_analyzed : [];
  const endpoints = Array.isArray(d.endpoints) ? d.endpoints : [];
  const secrets = Array.isArray(d.secrets) ? d.secrets : [];
  const sourceMaps = Array.isArray(d.source_maps) ? d.source_maps : [];

  const summaryHtml = `
    <div class="grid-4 gap-3 mb-4">
      <div class="stat-card">
        <div class="stat-value" style="font-size: 24px;">${jsFiles.length}</div>
        <div class="stat-label">JS Files</div>
      </div>
      <div class="stat-card">
        <div class="stat-value" style="font-size: 24px; color: var(--accent);">${endpoints.length}</div>
        <div class="stat-label">Endpoints</div>
      </div>
      <div class="stat-card">
        <div class="stat-value" style="font-size: 24px; color: ${secrets.length > 0 ? 'var(--danger)' : 'var(--success)'};">${secrets.length}</div>
        <div class="stat-label">Secrets</div>
      </div>
      <div class="stat-card">
        <div class="stat-value" style="font-size: 24px; color: ${sourceMaps.length > 0 ? 'var(--warning)' : 'var(--secondary)'};">${sourceMaps.length}</div>
        <div class="stat-label">Source Maps</div>
      </div>
    </div>
  `;

  let detailsHtml = "";

  if (secrets.length > 0) {
    detailsHtml += `
      <div class="card card-compact mb-4" style="border: 1px solid var(--danger); background: #fef2f2;">
        <div class="flex flex-between flex-center mb-3">
          <strong style="color: var(--danger);">⚠ Secrets Exposed in JavaScript</strong>
          <span class="badge badge-danger">${secrets.length}</span>
        </div>
        <table class="data-table">
          <thead><tr><th>Type</th><th>Preview</th><th>Location</th></tr></thead>
          <tbody>
            ${secrets.slice(0, 5).map((s: { secret_type?: string; value_preview?: string; source_file?: string; severity?: string }) => `
              <tr>
                <td><span class="badge badge-${s.severity === "critical" ? "critical" : "high"}" style="font-size: 10px;">${escapeHtml(s.secret_type || "secret")}</span></td>
                <td><code class="text-xs">${escapeHtml(s.value_preview || "***")}</code></td>
                <td class="text-xs text-muted">${escapeHtml((s.source_file || "").split("/").pop() || "")}</td>
              </tr>
            `).join("")}
          </tbody>
        </table>
      </div>
    `;
  }

  if (endpoints.length > 0) {
    detailsHtml += `
      <div class="mt-4">
        <p class="text-xs mb-2 font-medium">Discovered Endpoints (${endpoints.length})</p>
        <div class="flex gap-1 flex-wrap">
          ${endpoints.slice(0, 15).map((e: { path?: string; category?: string }) => `
            <span class="tag">${escapeHtml(e.path || "")}</span>
          `).join("")}
          ${endpoints.length > 15 ? `<span class="text-xs text-muted">+${endpoints.length - 15}</span>` : ""}
        </div>
      </div>
    `;
  }

  return summaryHtml + detailsHtml;
}

// Favicon Module
function generateFaviconHtml(d: Record<string, unknown>): string {
  const knownMatch = d.known_match as { name?: string; category?: string; risk_level?: string; cves?: string[] } | undefined;

  const summaryHtml = `
    <div class="grid-4 gap-3 mb-4">
      <div class="stat-card">
        <div style="font-size: 20px;">${d.favicon_found ? '<span style="color: var(--success);">✓</span>' : '<span style="color: var(--secondary);">✗</span>'}</div>
        <div class="stat-label">Found</div>
      </div>
      <div class="stat-card">
        <div class="mono text-sm" style="color: var(--accent);">${escapeHtml(String(d.hash_hex || d.hash || "-"))}</div>
        <div class="stat-label">Hash</div>
      </div>
      <div class="stat-card">
        <div class="stat-value" style="font-size: 16px;">${d.file_size ? `${((d.file_size as number) / 1024).toFixed(1)}KB` : "-"}</div>
        <div class="stat-label">Size</div>
      </div>
      <div class="stat-card">
        <div style="font-size: 14px;">${knownMatch ? `<span class="badge badge-${knownMatch.risk_level === "critical" ? "critical" : knownMatch.risk_level === "high" ? "high" : "info"}">${escapeHtml(knownMatch.name || "Match")}</span>` : '<span class="text-muted">Unknown</span>'}</div>
        <div class="stat-label">Match</div>
      </div>
    </div>
  `;

  let detailsHtml = "";

  if (knownMatch) {
    detailsHtml += `
      <div class="card card-compact" style="border-left: 3px solid ${knownMatch.risk_level === "critical" || knownMatch.risk_level === "high" ? "var(--danger)" : "var(--accent)"};">
        <div class="flex flex-between flex-center mb-2">
          <strong>${escapeHtml(knownMatch.name || "Known Match")}</strong>
          <div class="flex gap-2">
            <span class="badge badge-outline">${escapeHtml(knownMatch.category || "")}</span>
            <span class="badge badge-${knownMatch.risk_level === "critical" ? "critical" : knownMatch.risk_level === "high" ? "high" : "info"}">${knownMatch.risk_level || "info"} risk</span>
          </div>
        </div>
        ${knownMatch.cves && knownMatch.cves.length > 0 ? `
          <p class="text-xs text-muted mb-1">Associated CVEs:</p>
          <div class="flex gap-1 flex-wrap">
            ${knownMatch.cves.map(cve => `<span class="badge badge-danger" style="font-size: 10px;">${escapeHtml(cve)}</span>`).join("")}
          </div>
        ` : ""}
      </div>
    `;
  }

  if (d.shodan_query) {
    detailsHtml += `
      <div class="code-block mt-3">
        <p class="text-xs text-muted mb-1">Shodan Query:</p>
        <code>${escapeHtml(String(d.shodan_query))}</code>
      </div>
    `;
  }

  return summaryHtml + detailsHtml;
}

// IP Intel Module
function generateIpIntelHtml(d: Record<string, unknown>): string {
  const security = d.security as { is_vpn?: boolean; is_proxy?: boolean; is_tor?: boolean; threat_level?: string } | undefined;
  const network = d.network as { isp?: string; asn?: number; organization?: string } | undefined;
  const geolocation = d.geolocation as { city?: string; country_name?: string; country_code?: string } | undefined;

  const threatColors: Record<string, string> = {
    critical: "var(--danger)",
    high: "#ea580c",
    medium: "var(--warning)",
    low: "var(--accent)",
    unknown: "var(--secondary)",
  };

  const threatLevel = security?.threat_level || "unknown";

  return `
    <div class="grid-2 gap-4">
      <div class="card card-compact">
        <div class="flex flex-between flex-center mb-3">
          <strong class="text-sm">IP Address</strong>
          <span class="badge badge-outline">IPv${d.ip_version || 4}</span>
        </div>
        <code class="mono" style="font-size: 18px;">${escapeHtml(String(d.ip || ""))}</code>
        ${d.hostname ? `<p class="text-xs text-muted mt-1">${escapeHtml(String(d.hostname))}</p>` : ""}
      </div>
      <div class="card card-compact" style="border-left: 3px solid ${threatColors[threatLevel]};">
        <div class="flex flex-between flex-center mb-2">
          <strong class="text-sm">Threat Level</strong>
          <span class="badge badge-${threatLevel === "critical" || threatLevel === "high" ? "danger" : threatLevel === "medium" ? "medium" : "info"}">${threatLevel}</span>
        </div>
        <div class="flex gap-2 flex-wrap">
          ${security?.is_vpn ? '<span class="badge badge-medium">VPN</span>' : ""}
          ${security?.is_proxy ? '<span class="badge badge-medium">Proxy</span>' : ""}
          ${security?.is_tor ? '<span class="badge badge-danger">Tor</span>' : ""}
          ${!security?.is_vpn && !security?.is_proxy && !security?.is_tor ? '<span class="text-xs text-muted">No anonymization detected</span>' : ""}
        </div>
      </div>
    </div>
    <div class="grid-2 gap-4 mt-4">
      <div>
        <p class="text-xs text-muted mb-2 font-medium">Location</p>
        <p>${escapeHtml([geolocation?.city, geolocation?.country_name].filter(Boolean).join(", ") || "Unknown")}</p>
        ${geolocation?.country_code ? `<span class="badge badge-outline mt-1">${geolocation.country_code}</span>` : ""}
      </div>
      <div>
        <p class="text-xs text-muted mb-2 font-medium">Network</p>
        <p>${escapeHtml(network?.isp || network?.organization || "Unknown")}</p>
        ${network?.asn ? `<code class="text-xs mono">AS${network.asn}</code>` : ""}
      </div>
    </div>
  `;
}

// Typosquat Module
function generateTyposquatHtml(d: Record<string, unknown>): string {
  const typosquats = Array.isArray(d.typosquats) ? d.typosquats : [];
  const generatedCount = d.generated_count as number || 0;
  const registeredCount = d.registered_count as number || 0;

  const criticalCount = typosquats.filter((t: { risk?: string }) => t.risk === "critical").length;
  const highCount = typosquats.filter((t: { risk?: string }) => t.risk === "high").length;
  const withMx = typosquats.filter((t: { has_mx?: boolean }) => t.has_mx).length;

  const summaryHtml = `
    <div class="grid-4 gap-3 mb-4">
      <div class="stat-card">
        <div class="stat-value" style="font-size: 24px;">${formatIpCount(generatedCount)}</div>
        <div class="stat-label">Generated</div>
      </div>
      <div class="stat-card">
        <div class="stat-value" style="font-size: 24px; color: ${registeredCount > 0 ? 'var(--warning)' : 'var(--success)'};">${registeredCount}</div>
        <div class="stat-label">Registered</div>
      </div>
      <div class="stat-card">
        <div class="stat-value" style="font-size: 24px; color: ${criticalCount + highCount > 0 ? 'var(--danger)' : 'var(--success)'};">${criticalCount + highCount}</div>
        <div class="stat-label">High Risk</div>
      </div>
      <div class="stat-card">
        <div class="stat-value" style="font-size: 24px; color: ${withMx > 0 ? 'var(--warning)' : 'var(--secondary)'};">${withMx}</div>
        <div class="stat-label">With Email</div>
      </div>
    </div>
  `;

  if (typosquats.length === 0) {
    return summaryHtml + '<p class="text-sm text-muted">No typosquats detected</p>';
  }

  const registeredTypos = typosquats.filter((t: { is_registered?: boolean }) => t.is_registered);

  return summaryHtml + `
    <table class="data-table">
      <thead><tr><th>Domain</th><th>Technique</th><th>Risk</th><th>Email</th></tr></thead>
      <tbody>
        ${registeredTypos.slice(0, 15).map((t: { domain?: string; technique?: string; risk?: string; has_mx?: boolean }) => `
          <tr>
            <td><code class="mono text-xs">${escapeHtml(t.domain || "")}</code></td>
            <td><span class="badge badge-outline" style="font-size: 10px;">${escapeHtml(t.technique || "")}</span></td>
            <td><span class="badge badge-${t.risk === "critical" ? "critical" : t.risk === "high" ? "high" : t.risk === "medium" ? "medium" : "info"}">${t.risk || "info"}</span></td>
            <td>${t.has_mx ? '<span class="badge badge-medium">MX</span>' : '<span class="text-muted">—</span>'}</td>
          </tr>
        `).join("")}
      </tbody>
    </table>
    ${registeredTypos.length > 15 ? `<p class="text-xs text-muted mt-2">... and ${registeredTypos.length - 15} more registered typosquats</p>` : ""}
  `;
}

// Generic Module (fallback)
function generateGenericHtml(d: Record<string, unknown>): string {
  const keys = Object.keys(d).filter(k => k !== "error" && k !== "duration_ms" && k !== "score");
  if (keys.length === 0) return '<p class="text-sm text-muted">No data available</p>';

  return keys.slice(0, 12).map(key => {
    let value = d[key];
    if (Array.isArray(value)) {
      return `
        <div class="data-row">
          <div class="data-label">${escapeHtml(key)}</div>
          <div class="data-value">${value.length} items</div>
        </div>
      `;
    }
    if (typeof value === "object" && value !== null) {
      value = JSON.stringify(value).substring(0, 80) + "...";
    }
    if (typeof value === "string" && value.length > 80) value = value.substring(0, 80) + "...";
    return `
      <div class="data-row">
        <div class="data-label">${escapeHtml(key)}</div>
        <div class="data-value mono text-xs">${escapeHtml(String(value))}</div>
      </div>
    `;
  }).join("");
}

// ============================================================================
// PDF EXPORT
// ============================================================================

function openForPrint(htmlContent: string): void {
  const blob = new Blob([htmlContent], { type: "text/html" });
  const blobUrl = URL.createObjectURL(blob);
  const printWindow = window.open(blobUrl, "_blank");

  if (!printWindow) {
    URL.revokeObjectURL(blobUrl);
    downloadFile(htmlContent, "security-report.html", "text/html");
    return;
  }

  printWindow.onload = () => {
    URL.revokeObjectURL(blobUrl);
    printWindow.print();
  };
}

// ============================================================================
// MAIN EXPORT FUNCTION
// ============================================================================

export function exportDetailedReport(
  scan: Scan,
  results: ScanResult,
  format: DetailedExportFormat
): void {
  const metadata: DetailedExportMetadata = {
    target: scan.target,
    scanId: scan.id,
    mode: scan.mode,
    modules: scan.modules,
    scannedAt: scan.created_at,
    completedAt: scan.completed_at,
    exportedAt: new Date().toISOString(),
  };

  const safeTarget = metadata.target.replace(/[^a-zA-Z0-9.-]/g, "_");
  const dateStr = new Date(metadata.scannedAt).toISOString().split("T")[0];

  switch (format) {
    case "json": {
      const content = generateJson(results, metadata);
      downloadFile(content, `detailed-report-${safeTarget}-${dateStr}.json`, "application/json");
      break;
    }

    case "csv": {
      const content = generateCsv(results, metadata);
      downloadFile(content, `findings-${safeTarget}-${dateStr}.csv`, "text/csv");
      break;
    }

    case "html": {
      const content = generateHtml(results, metadata);
      downloadFile(content, `detailed-report-${safeTarget}-${dateStr}.html`, "text/html");
      break;
    }

    case "pdf": {
      const content = generateHtml(results, metadata);
      openForPrint(content);
      break;
    }
  }
}

export function getDetailedExportFormats(): Array<{
  format: DetailedExportFormat;
  label: string;
  description: string;
}> {
  return [
    { format: "pdf", label: "PDF Report", description: "Full report with findings" },
    { format: "html", label: "HTML Report", description: "Complete technical report" },
    { format: "json", label: "JSON Data", description: "Raw data with all modules" },
    { format: "csv", label: "CSV Findings", description: "Findings for spreadsheets" },
  ];
}
